if (!customElements.get('wls-article-content')) {
    class WLSArticleContent extends HTMLElement {
        constructor() {
            super();

            this.selectors = {
                body: '.js-wls-article-content-body',
                sidebar: '.js-wls-article-content-toc-side',
                tocList: '.js-wls-article-content-toc-list',
                shareBtn: '.js-wls-article-content-share-btn',
                shareLinks: '.js-wls-article-content-share-links'
            };

            this.headings = [];
            this.links = [];
            this.activeIndex = -1;
            this.ticking = false;

            this.onScroll = this.onScroll.bind(this);
            this.handleScroll = this.handleScroll.bind(this);
        }

        connectedCallback() {
            this.body = this.querySelector(this.selectors.body);
            this.sidebar = this.querySelector(this.selectors.sidebar);
            this.tocList = this.querySelector(this.selectors.tocList);
            this.shareBtn = this.querySelector(this.selectors.shareBtn);

            this.offset = parseInt(this.dataset.stickyOffset, 10) || 150;
            this.level = (this.dataset.tocLevel || 'h2').toLowerCase();

            this.hasToc = this.buildToc();
            this.initShare();

            window.addEventListener('scroll', this.handleScroll, { passive: true });
            window.addEventListener('resize', this.handleScroll);

            this.onScroll();
        }

        disconnectedCallback() {
            window.removeEventListener('scroll', this.handleScroll);
            window.removeEventListener('resize', this.handleScroll);
        }

        slugify(text) {
            return text
                .toString()
                .toLowerCase()
                .trim()
                .replace(/[^\w\s-]/g, '')
                .replace(/[\s_]+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-+|-+$/g, '');
        }

        buildToc() {
            if (!this.body || !this.tocList) return false;

            const nodes = Array.from(this.body.querySelectorAll(this.level));
            if (nodes.length === 0) return false;

            const used = {};

            nodes.forEach((node, index) => {
                let id = node.id || this.slugify(node.textContent) || 'section-' + (index + 1);
                let unique = id;
                let suffix = 2;
                while (used[unique]) {
                    unique = id + '-' + suffix;
                    suffix += 1;
                }
                used[unique] = true;
                node.id = unique;

                const item = document.createElement('li');
                item.className = 'wls-article-content__toc-item';

                const link = document.createElement('a');
                link.className = 'wls-article-content__toc-link';
                link.href = '#' + unique;
                link.textContent = node.textContent;
                link.addEventListener('click', (event) => {
                    event.preventDefault();
                    this.scrollToHeading(node);
                });

                item.appendChild(link);
                this.tocList.appendChild(item);

                this.headings.push(node);
                this.links.push(link);
            });

            if (this.sidebar) this.sidebar.hidden = false;
            this.setActive(0);

            return true;
        }

        scrollToHeading(node) {
            const top = node.getBoundingClientRect().top + window.scrollY - this.offset;
            window.scrollTo({ top: top, behavior: 'smooth' });
        }

        setActive(index) {
            if (index === this.activeIndex) return;
            this.activeIndex = index;
            this.links.forEach((link, i) => link.classList.toggle('is-active', i === index));
        }

        handleScroll() {
            if (this.ticking) return;
            this.ticking = true;
            window.requestAnimationFrame(() => {
                this.onScroll();
                this.ticking = false;
            });
        }

        onScroll() {
            // Active section highlighting.
            if (this.hasToc && this.headings.length) {
                let active = 0;
                for (let i = 0; i < this.headings.length; i += 1) {
                    if (this.headings[i].getBoundingClientRect().top - this.offset - 1 <= 0) {
                        active = i;
                    } else {
                        break;
                    }
                }
                this.setActive(active);
            }
        }

        initShare() {
            if (!this.shareBtn) return;
            const links = this.shareBtn.querySelector(this.selectors.shareLinks);
            if (!links) return;

            const url = links.dataset.url || window.location.href;
            const title = links.dataset.title || document.title;
            const openPopup = (target) => window.open(target, '_blank', 'width=800,height=600');

            const facebook = links.querySelector('[data-platform="facebook"]');
            if (facebook) {
                facebook.addEventListener('click', () => {
                    openPopup('https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url));
                });
            }

            const twitter = links.querySelector('[data-platform="twitter"]');
            if (twitter) {
                twitter.addEventListener('click', () => {
                    openPopup('https://x.com/intent/tweet?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(title));
                });
            }

            const linkedin = links.querySelector('[data-platform="linkedin"]');
            if (linkedin) {
                linkedin.addEventListener('click', () => {
                    openPopup('https://www.linkedin.com/shareArticle?mini=true&url=' + encodeURIComponent(url));
                });
            }

            const email = links.querySelector('[data-platform="email"]');
            if (email) {
                email.addEventListener('click', () => {
                    const subject = encodeURIComponent('Check out this article: ' + title);
                    const body = encodeURIComponent('I found this article interesting and thought you might like it:\n' + url);
                    window.location.href = 'mailto:?subject=' + subject + '&body=' + body;
                });
            }
        }
    }

    customElements.define('wls-article-content', WLSArticleContent);
}
